/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useContext, useCallback } from 'react';
import api from '../services/api';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import ManualQuizCreator from '../components/ManualQuizCreator';
import {
  DocumentTextIcon,
  ClockIcon,
  AcademicCapIcon,
  ArrowLeftIcon,
  CloudArrowUpIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const QuizFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isEdit = Boolean(id);
  const [title, setTitle] = useState('');
  const [courseId, setCourseId] = useState('');
  const [file, setFile] = useState(null);
  const [courses, setCourses] = useState([]);
  const [openTime, setOpenTime] = useState('');
  const [closeTime, setCloseTime] = useState('');
  const [timeLimit, setTimeLimit] = useState('');
  const [timedSections, setTimedSections] = useState('');
  const [useTimedSections, setUseTimedSections] = useState(false);
  const [sections, setSections] = useState([]);
  const [questionsPerPage, setQuestionsPerPage] = useState('');
  const [studentType, setStudentType] = useState('');
  const [maxAttempts, setMaxAttempts] = useState('');
  const [resetTimerOnAttempt, setResetTimerOnAttempt] = useState(true);
  const [isRanked, setIsRanked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [creationMode, setCreationMode] = useState(() => {
    // Load creation mode from localStorage
    const savedMode = localStorage.getItem('quiz_draft_creation_mode');
    console.log('🔍 Loading creation mode from localStorage:', savedMode);
    return savedMode || 'upload';
  });
  const [manualQuestions, setManualQuestions] = useState(() => {
    // Load draft from localStorage on initial render
    const draftKey = `quiz_draft_manual_questions`;
    const saved = localStorage.getItem(draftKey);
    console.log('🔍 Loading manual questions from localStorage:', saved ? JSON.parse(saved).length + ' questions' : 'none');
    return saved ? JSON.parse(saved) : [];
  });

  const validateForm = () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = 'Title is required';
    if (!courseId) newErrors.courseId = 'Course selection is required';
    if (!studentType) newErrors.studentType = 'Student type is required';
    
    // Validate content based on creation mode
    if (!isEdit) {
      if (creationMode === 'upload' && !file) {
        newErrors.file = 'Please upload a PDF file or switch to manual creation';
      } else if (creationMode === 'manual' && manualQuestions.length === 0) {
        newErrors.questions = 'Please create at least one question';
      }
    }
    
    if (openTime && closeTime && new Date(openTime) >= new Date(closeTime)) {
      newErrors.closeTime = 'Close time must be after open time';
    }
    if (timeLimit && (parseInt(timeLimit) < 1 || parseInt(timeLimit) > 600)) {
      newErrors.timeLimit = 'Time limit must be between 1 and 600 minutes';
    }
    if (maxAttempts && (parseInt(maxAttempts) < 1 || parseInt(maxAttempts) > 10)) {
      newErrors.maxAttempts = 'Max attempts must be between 1 and 10';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper functions for timed sections
  const addSection = () => {
    setSections([...sections, { name: '', timeLimit: '', questionStart: '', questionEnd: '' }]);
  };

  const removeSection = (index) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const updateSection = (index, field, value) => {
    const updated = [...sections];
    updated[index][field] = value;
    setSections(updated);
  };

  // Sync sections to timedSections JSON
  useEffect(() => {
    if (useTimedSections && sections.length > 0) {
      const validSections = sections.filter(s => s.name && s.timeLimit && s.questionStart && s.questionEnd);
      if (validSections.length > 0) {
        const formatted = validSections.map(s => {
          return {
            name: s.name,
            start: parseInt(s.questionStart),
            end: parseInt(s.questionEnd),
            limit: parseInt(s.timeLimit)
          };
        });
        setTimedSections(JSON.stringify(formatted, null, 2));
      } else {
        setTimedSections('');
      }
    } else {
      // Clear timedSections if disabled OR if no sections exist
      setTimedSections('');
    }
  }, [useTimedSections, sections]);

  // Save creation mode to localStorage
  useEffect(() => {
    console.log('💾 Saving creation mode to localStorage:', creationMode);
    localStorage.setItem('quiz_draft_creation_mode', creationMode);
  }, [creationMode]);

  // Save manual questions to localStorage as draft
  useEffect(() => {
    console.log('📝 manualQuestions changed:', {
      mode: creationMode,
      count: manualQuestions.length,
      questions: manualQuestions
    });
    
    if (creationMode === 'manual') {
      const draftKey = `quiz_draft_manual_questions`;
      console.log('💾 Saving manual questions to localStorage:', manualQuestions.length, 'questions');
      localStorage.setItem(draftKey, JSON.stringify(manualQuestions));
      console.log('✅ Draft saved successfully to localStorage');
    } else {
      console.log('⏭️ Skipping save - mode is:', creationMode);
    }
  }, [manualQuestions, creationMode]);

  useEffect(() => {
    // load courses for dropdown
    api.get('/courses', { params: { limit: 1000 } })
      .then(res => setCourses(res.data.data || res.data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (isEdit) {
      api.get(`/quizzes/${id}`)
        .then(res => {
          setTitle(res.data.title);
          setCourseId(res.data.courseId);
          setStudentType(res.data.studentType); // Fetch and set studentType
          setOpenTime(res.data.openTime ? res.data.openTime.slice(0,16) : '');
          setCloseTime(res.data.closeTime ? res.data.closeTime.slice(0,16) : '');
          setTimeLimit(res.data.timeLimit ? String(res.data.timeLimit) : '');
          setQuestionsPerPage(res.data.questionsPerPage ? String(res.data.questionsPerPage) : '');
          setMaxAttempts(res.data.maxAttempts ? String(res.data.maxAttempts) : '');
          setResetTimerOnAttempt(res.data.resetTimerOnAttempt !== undefined ? res.data.resetTimerOnAttempt : true);
          setIsRanked(Boolean(res.data.isRanked));
          
          // Load timed sections
          if (res.data.timedSections && Array.isArray(res.data.timedSections) && res.data.timedSections.length > 0) {
            setUseTimedSections(true);
            const loadedSections = res.data.timedSections.map(section => ({
              name: section.name || '',
              timeLimit: String(section.limit || ''),
              questionStart: String(section.start || ''),
              questionEnd: String(section.end || '')
            }));
            setSections(loadedSections);
            setTimedSections(JSON.stringify(res.data.timedSections, null, 2));
          } else {
            setUseTimedSections(false);
            setSections([]);
            setTimedSections('');
          }
        })
        .catch(console.error);
    }
  }, [id, isEdit]);

  // Stable callback for ManualQuizCreator
  const handleQuestionsCreated = useCallback((questions) => {
    console.log('📥📥📥 PARENT RECEIVED questions from ManualQuizCreator:', questions.length, new Date().toLocaleTimeString());
    console.log('Questions data:', questions);
    setManualQuestions(questions);
    setErrors(prev => ({ ...prev, questions: null }));
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    setErrors({});
    
    try {
      if (isEdit) {
        // For edit mode, use FormData (same as before)
        const formData = new FormData();
        formData.append('title', title);
        formData.append('courseId', courseId);
        if (file) formData.append('pdf', file);
        formData.append('openTime', openTime);
        formData.append('closeTime', closeTime);
        formData.append('timeLimit', timeLimit);
        
        if (timedSections && timedSections.trim()) {
          try {
            const parsedSections = JSON.parse(timedSections);
            formData.append('timedSections', JSON.stringify(parsedSections));
          } catch (e) {
            formData.append('timedSections', JSON.stringify([]));
          }
        } else {
          formData.append('timedSections', JSON.stringify([]));
        }
        
        formData.append('questionsPerPage', questionsPerPage);
        formData.append('studentType', studentType);
        formData.append('maxAttempts', maxAttempts);
        formData.append('resetTimerOnAttempt', resetTimerOnAttempt);
  formData.append('isRanked', isRanked);
        
        await api.put(`/quizzes/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        // For create mode, check if using manual questions or PDF upload
        if (creationMode === 'manual' && manualQuestions.length > 0) {
          // Use the from-questions endpoint for manual creation
          const payload = {
            title,
            courseId,
            openTime,
            closeTime,
            timeLimit,
            timedSections: timedSections && timedSections.trim() ? JSON.parse(timedSections) : [],
            questionsPerPage,
            studentType,
            maxAttempts,
            resetTimerOnAttempt,
            isRanked,
            questions: manualQuestions
          };
          await api.post('/quizzes/from-questions', payload);
        } else {
          // Use the standard multipart form data upload for PDF
          const formData = new FormData();
          formData.append('title', title);
          formData.append('courseId', courseId);
          if (file) formData.append('pdf', file);
          formData.append('openTime', openTime);
          formData.append('closeTime', closeTime);
          formData.append('timeLimit', timeLimit);
          
          if (timedSections && timedSections.trim()) {
            try {
              const parsedSections = JSON.parse(timedSections);
              formData.append('timedSections', JSON.stringify(parsedSections));
            } catch (e) {
              formData.append('timedSections', JSON.stringify([]));
            }
          } else {
            formData.append('timedSections', JSON.stringify([]));
          }
          
          formData.append('questionsPerPage', questionsPerPage);
          formData.append('studentType', studentType);
          formData.append('maxAttempts', maxAttempts);
          formData.append('resetTimerOnAttempt', resetTimerOnAttempt);
          formData.append('isRanked', isRanked);
          
          await api.post('/quizzes', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
      }
      
      // Clear draft from localStorage on successful submission
      localStorage.removeItem('quiz_draft_manual_questions');
      localStorage.removeItem('quiz_draft_creation_mode');
      
      setSuccess(true);
      setTimeout(() => {
        // Navigate based on user role
        const quizzesPath = user?.role === 'admin' ? '/admin/dashboard/quizzes' : '/teachers/dashboard/quizzes';
        navigate(quizzesPath);
      }, 1500);
    } catch (err) {
      console.error(err);
      setErrors({ submit: err.response?.data?.error || 'Error saving quiz. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center">
              <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
              <span className="text-green-800 font-medium">
                Quiz {isEdit ? 'updated' : 'created'} successfully! Redirecting...
              </span>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => {
                  const quizzesPath = user?.role === 'admin' ? '/admin/dashboard/quizzes' : '/teachers/dashboard/quizzes';
                  navigate(quizzesPath);
                }}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                  <DocumentTextIcon className="w-8 h-8 text-blue-600 mr-3" />
                  {isEdit ? 'Edit Quiz' : 'Create New Quiz'}
                </h1>
                <p className="text-gray-600 mt-1">
                  {isEdit ? 'Update quiz details and settings' : 'Set up a new quiz for your students'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {errors.submit && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mr-2" />
              <span className="text-red-800">{errors.submit}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <InformationCircleIcon className="w-5 h-5 text-blue-600 mr-2" />
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Quiz Title *</label>
                <input
                  type="text"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 ${
                    errors.title ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  } ${isEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Enter quiz title"
                  required
                  readOnly={isEdit}
                  disabled={isEdit}
                />
                {isEdit && <p className="mt-1 text-xs text-gray-500">Quiz title cannot be changed after creation</p>}
                {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Course *</label>
                <select
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 ${
                    errors.courseId ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  } ${isEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  value={courseId}
                  onChange={e => setCourseId(e.target.value)}
                  required
                  disabled={isEdit}
                >
                  <option value="">Select a course</option>
                  {courses.map(c => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </select>
                {isEdit && <p className="mt-1 text-xs text-gray-500">Course cannot be changed after creation</p>}
                {errors.courseId && <p className="mt-1 text-sm text-red-600">{errors.courseId}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Student Level *</label>
                <select
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 ${
                    errors.studentType ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  } ${isEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  value={studentType}
                  onChange={e => setStudentType(e.target.value)}
                  required
                  disabled={isEdit}
                >
                  <option value="">Select student level</option>
                  <option value="shs1">Senior High School 1</option>
                  <option value="shs2">Senior High School 2</option>
                  <option value="shs3">Senior High School 3</option>
                </select>
                {isEdit && <p className="mt-1 text-xs text-gray-500">Student level cannot be changed after creation</p>}
                {errors.studentType && <p className="mt-1 text-sm text-red-600">{errors.studentType}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quiz Content
                  {!isEdit && <span className="text-red-500"> *</span>}
                </label>
                
                {/* Mode Selector - Only show when creating new quiz */}
                {!isEdit && (
                  <div className="mb-4 flex space-x-2 bg-gray-100 p-1 rounded-lg">
                    <button
                      type="button"
                      onClick={() => {
                        setCreationMode('upload');
                        // Don't clear manual questions - keep them as draft
                      }}
                      className={`flex-1 px-4 py-2 rounded-md font-medium transition-all ${
                        creationMode === 'upload'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <CloudArrowUpIcon className="w-5 h-5 inline mr-2" />
                      Upload PDF
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCreationMode('manual');
                        setFile(null);
                      }}
                      className={`flex-1 px-4 py-2 rounded-md font-medium transition-all ${
                        creationMode === 'manual'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <PlusIcon className="w-5 h-5 inline mr-2" />
                      Create Manually
                    </button>
                  </div>
                )}

                {/* PDF Upload Section */}
                {(isEdit || creationMode === 'upload') && (
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-gray-400 transition-colors duration-200">
                    <div className="space-y-1 text-center">
                      <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                          <span>Upload a PDF file</span>
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={e => setFile(e.target.files[0])}
                            className="sr-only"
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">PDF up to 10MB</p>
                      {file && (
                        <p className="text-sm text-green-600 font-medium">
                          ✓ Selected: {file.name}
                        </p>
                      )}
                      {isEdit && <p className="mt-1 text-xs text-gray-500">Upload new file to replace existing questions</p>}
                    </div>
                  </div>
                )}

                {/* Manual Creation Section */}
                {!isEdit && creationMode === 'manual' && (
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6">
                    {/* Draft Indicator */}
                    {manualQuestions.length > 0 && (
                      <div className="mb-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-center">
                          <InformationCircleIcon className="w-5 h-5 text-blue-600 mr-2" />
                          <span className="text-sm text-blue-800">
                            <strong>Draft saved:</strong> {manualQuestions.length} question{manualQuestions.length !== 1 ? 's' : ''} (auto-saves)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm('Clear all draft questions? This cannot be undone.')) {
                              setManualQuestions([]);
                              localStorage.removeItem('quiz_draft_manual_questions');
                            }
                          }}
                          className="text-sm text-red-600 hover:text-red-800 font-medium"
                        >
                          Clear Draft
                        </button>
                      </div>
                    )}
                    
                    <ManualQuizCreator
                      onQuestionsCreated={handleQuestionsCreated}
                      initialQuestions={manualQuestions.map(q => {
                        // Restore original format if available
                        if (q._original) {
                          return q._original;
                        }
                        // Otherwise keep as is
                        return q;
                      })}
                    />
                  </div>
                )}

                {errors.file && <p className="mt-2 text-sm text-red-600">{errors.file}</p>}
                {errors.questions && <p className="mt-2 text-sm text-red-600">{errors.questions}</p>}
              </div>
            </div>
          </div>

          {/* Timing & Schedule */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <ClockIcon className="w-5 h-5 text-blue-600 mr-2" />
              Timing & Schedule
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Open Time</label>
                <input
                  type="datetime-local"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                  value={openTime}
                  onChange={e => setOpenTime(e.target.value)}
                />
                <p className="mt-1 text-xs text-gray-500">When students can start taking the quiz</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Close Time</label>
                <input
                  type="datetime-local"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 ${
                    errors.closeTime ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  value={closeTime}
                  onChange={e => setCloseTime(e.target.value)}
                />
                {errors.closeTime && <p className="mt-1 text-sm text-red-600">{errors.closeTime}</p>}
                <p className="mt-1 text-xs text-gray-500">When the quiz becomes unavailable</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time Limit (minutes)</label>
                <input
                  type="number"
                  min="1"
                  max="600"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 ${
                    errors.timeLimit ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  value={timeLimit}
                  onChange={e => setTimeLimit(e.target.value)}
                  placeholder="e.g., 60"
                />
                {errors.timeLimit && <p className="mt-1 text-sm text-red-600">{errors.timeLimit}</p>}
                <p className="mt-1 text-xs text-gray-500">Leave empty for no time limit</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Attempts</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 ${
                    errors.maxAttempts ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  value={maxAttempts}
                  onChange={e => setMaxAttempts(e.target.value)}
                  placeholder="e.g., 3"
                />
                {errors.maxAttempts && <p className="mt-1 text-sm text-red-600">{errors.maxAttempts}</p>}
                <p className="mt-1 text-xs text-gray-500">Leave empty for unlimited attempts</p>
              </div>

              <div className="md:col-span-2">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="resetTimerOnAttempt"
                    checked={resetTimerOnAttempt}
                    onChange={e => setResetTimerOnAttempt(e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <label htmlFor="resetTimerOnAttempt" className="text-sm font-medium text-gray-700">
                    Reset timer for each attempt
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500 ml-7">
                  When enabled, each new attempt starts with the full time limit. When disabled, time continues from previous attempts.
                </p>
              </div>

              <div className="md:col-span-2">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="isRanked"
                    checked={isRanked}
                    onChange={e => setIsRanked(e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <label htmlFor="isRanked" className="text-sm font-medium text-gray-700">
                    Rank students for this quiz (first to last)
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500 ml-7">
                  When enabled, students will see their ranking position for this quiz on the results page.
                </p>
              </div>
            </div>
          </div>

          {/* Advanced Settings */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <AcademicCapIcon className="w-5 h-5 text-blue-600 mr-2" />
              Advanced Settings
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Questions Per Page</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                  value={questionsPerPage}
                  onChange={e => setQuestionsPerPage(e.target.value)}
                  placeholder="e.g., 5"
                />
                <p className="mt-1 text-xs text-gray-500">How many questions to show per page</p>
              </div>

              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">Timed Sections</label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="useTimedSections"
                      checked={useTimedSections}
                      onChange={e => setUseTimedSections(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <label htmlFor="useTimedSections" className="text-sm font-medium text-gray-700">
                      Enable section-based timing
                    </label>
                  </div>
                </div>

                {useTimedSections ? (
                  <div className="space-y-4">
                    {sections.map((section, index) => (
                      <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-gray-700">Section {index + 1}</h4>
                          <button
                            type="button"
                            onClick={() => removeSection(index)}
                            className="text-red-600 hover:text-red-700 transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Section Name</label>
                            <input
                              type="text"
                              value={section.name}
                              onChange={e => updateSection(index, 'name', e.target.value)}
                              placeholder="e.g., Multiple Choice"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Time Limit (minutes)</label>
                            <input
                              type="number"
                              min="1"
                              value={section.timeLimit}
                              onChange={e => updateSection(index, 'timeLimit', e.target.value)}
                              placeholder="e.g., 30"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Question Start #</label>
                            <input
                              type="number"
                              min="1"
                              value={section.questionStart}
                              onChange={e => updateSection(index, 'questionStart', e.target.value)}
                              placeholder="e.g., 1"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Question End #</label>
                            <input
                              type="number"
                              min="1"
                              value={section.questionEnd}
                              onChange={e => updateSection(index, 'questionEnd', e.target.value)}
                              placeholder="e.g., 10"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={addSection}
                        className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors duration-200 flex items-center justify-center"
                      >
                        <PlusIcon className="w-5 h-5 mr-2" />
                        Add Section
                      </button>

                      {sections.length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setSections([]);
                            setTimedSections('');
                          }}
                          className="w-full py-3 border-2 border-red-300 rounded-lg text-red-600 hover:border-red-500 hover:bg-red-50 transition-colors duration-200 flex items-center justify-center"
                        >
                          <TrashIcon className="w-5 h-5 mr-2" />
                          Clear All Sections
                        </button>
                      )}
                    </div>

                    {sections.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-start">
                          <InformationCircleIcon className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                          <div className="text-xs text-blue-700">
                            <p className="font-medium mb-1">Preview:</p>
                            <pre className="bg-white rounded p-2 overflow-x-auto text-xs">
                              {timedSections || 'Fill in all section fields to see preview'}
                            </pre>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    Enable section-based timing to divide your quiz into multiple timed sections. 
                    Each section can have its own time limit and question range.
                  </p>
                )}
              </div>

              <div className="md:col-span-2">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <InformationCircleIcon className="w-5 h-5 text-blue-600 mr-2" />
                    <h3 className="text-sm font-medium text-blue-800">Question Ordering</h3>
                  </div>
                  <p className="mt-1 text-sm text-blue-700">
                    Quiz questions are automatically shuffled for each student to ensure fairness. 
                    Questions saved to the question bank maintain their original order for practice sessions.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  const quizzesPath = user?.role === 'admin' ? '/admin/dashboard/quizzes' : '/teachers/dashboard/quizzes';
                  navigate(quizzesPath);
                }}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors duration-200 flex items-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isEdit ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="w-5 h-5 mr-2" />
                    {isEdit ? 'Update Quiz' : 'Create Quiz'}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuizFormPage;
